/**
 * Keeping something at the top of the list.
 *
 * An agency running four hundred units is not looking at four hundred units — it is dealing with
 * six, and the rest are background. A pin says which six, and it says it in the place the answer is
 * needed: the list itself, sorted so the pinned ones lead.
 *
 * **The state is sent, not toggled.** Two people looking at the same list on two phones both press
 * the pin; asking the server to flip whatever it holds leaves the row in whichever state the second
 * press implied, which is a coin toss. Sending "pinned: true" means the second press is a no-op and
 * everybody's list agrees.
 *
 * **It moves before the answer comes back**, and moves back if the server disagrees, for the same
 * reason the heart on the listing site does: a control that does nothing for as long as the network
 * takes is a control nobody believes is connected to anything.
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { IconPushpin } from "./icons";
import { useSession } from "@/lib/session";
import { useRecordLock } from "@/lib/gating";

interface Props {
  /** `property` or `unit` — the two things that can be pinned, on the same route shape. */
  kind: "property" | "unit";
  id: string;
  pinnedAt: string | null | undefined;
  /** A row in a list, or a control on the thing's own page. */
  size?: "sm" | "md";
  /** Query keys to refresh once it has landed, so the list re-sorts. */
  invalidates?: string[][];
}

export function PinButton({ kind, id, pinnedAt, size = "sm", invalidates = [] }: Props) {
  const { can } = useSession();
  const queryClient = useQueryClient();
  const lock = useRecordLock(kind === "property" ? "properties" : "units");
  const [pinned, setPinned] = useState(Boolean(pinnedAt));
  const [busy, setBusy] = useState(false);

  // Deciding what the agency should be looking at is the same authority as deciding what is on the
  // market — so it is offered to whoever can edit, and to nobody else.
  if (!can("property.edit")) return null;

  /*
    Above what the plan opens, the pin goes with the rest of the writes.

    The API refuses `POST /:id/pin` on a padlocked record — ADR-0106 includes pinning deliberately,
    because a padlocked record holding the top of somebody's list is the padlock shouting. Left
    here, this control would move, be refused, and silently move back, which is the worst of the
    three possible behaviours. The row already carries the padlock beside its name and that padlock
    says why and what would open it, so this says nothing a second time and stands down.
  */
  if (lock.isLocked(id)) return null;

  const press = async (event: React.MouseEvent) => {
    // Cards are links. Without this the pin opens the thing it was meant to mark.
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;

    const next = !pinned;
    setPinned(next);
    setBusy(true);
    try {
      await api.post(`/${kind === "property" ? "properties" : "units"}/${id}/pin`, { pinned: next });
      for (const key of invalidates) await queryClient.invalidateQueries({ queryKey: key });
    } catch {
      setPinned(!next);
    } finally {
      setBusy(false);
    }
  };

  const label = pinned ? "Unpin — stop keeping this at the top" : "Pin — keep this at the top of the list";

  return (
    <button
      type="button"
      onClick={press}
      aria-pressed={pinned}
      aria-label={label}
      title={pinned ? "Pinned" : "Pin to the top"}
      className={`inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[var(--radius-control)] border transition-colors ${
        size === "md" ? "px-3 py-2 text-sm font-medium" : "h-7 w-7"
      } ${
        pinned
          ? "border-warn-200 bg-warn-50 text-warn-700 hover:bg-warn-100"
          : "border-transparent text-ink-400 hover:border-ink-200 hover:bg-ink-50 hover:text-ink-700"
      }`}
    >
      <IconPushpin size={size === "md" ? 16 : 15} />
      {size === "md" ? <span>{pinned ? "Pinned" : "Pin"}</span> : null}
    </button>
  );
}

/** The mark a pinned row carries, for lists that show state rather than offer a control. */
export function PinnedMark({ pinnedAt }: { pinnedAt: string | null | undefined }) {
  if (!pinnedAt) return null;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] bg-warn-50 px-2 py-0.5 text-xs font-semibold text-warn-700"
      title="Pinned to the top of the list"
    >
      <IconPushpin size={12} />
      Pinned
    </span>
  );
}
